import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, BellOff, BookmarkPlus, Pencil, Trash2 } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { EmptyState } from "@/components/states";
import { useAuth } from "@/lib/auth";
import {
  deleteSavedSearch,
  describeSavedSearch,
  listSavedSearches,
  renameSavedSearch,
  setSavedSearchAlerts,
  type SavedSearch,
} from "@/lib/saved-searches";

export const Route = createFileRoute("/_authenticated/customer/saved-searches")({
  head: () => ({
    meta: [
      { title: "Saved Searches · Landoo" },
      {
        name: "description",
        content:
          "Manage your saved Landoo property searches and turn in-app search alerts on or off for new matching listings.",
      },
      { property: "og:title", content: "Saved Searches · Landoo" },
      {
        property: "og:description",
        content: "Save search filters and get notified when new listings match.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SavedSearchesPage,
});

type Tab = "all" | "alerts_on" | "alerts_off";

function SavedSearchesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [renaming, setRenaming] = useState<SavedSearch | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const key = ["customer", "saved-searches", user?.id];

  const { data: rows = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => listSavedSearches(user!.id),
    enabled: !!user,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["customer", "saved-searches"] });

  const alertM = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setSavedSearchAlerts(id, enabled),
    onSuccess: (_d, v) => {
      toast.success(v.enabled ? "Alert enabled." : "Alert disabled.");
      invalidate();
    },
    onError: () => toast.error("Could not update this alert."),
  });

  const renameM = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameSavedSearch(id, name),
    onSuccess: () => {
      toast.success("Search renamed.");
      setRenaming(null);
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not rename this search."),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => deleteSavedSearch(id),
    onSuccess: () => {
      toast.success("Saved search deleted.");
      invalidate();
    },
    onError: () => toast.error("Could not delete this search."),
  });

  const visible = useMemo(() => {
    if (tab === "alerts_on") return rows.filter((r) => r.alerts_enabled);
    if (tab === "alerts_off") return rows.filter((r) => !r.alerts_enabled);
    return rows;
  }, [rows, tab]);

  return (
    <DashboardShell title="Saved Searches" role="Customer" items={CUSTOMER_NAV}>
      <div className="space-y-4">
        <header className="rounded-3xl border border-border bg-card p-4 shadow-soft">
          <h1 className="text-lg font-bold text-brown-dark">Saved Searches</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Save purpose, property type, city, area, price range and distance. With alerts on you
            get an in-app notification whenever a newly approved listing matches — no emails.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-xl bg-brand px-3 py-2 text-sm font-semibold text-brown-dark shadow-soft transition hover:brightness-105"
            >
              <BookmarkPlus className="h-4 w-4" /> Save a new search on the map
            </Link>
          </div>
        </header>

        <div className="flex flex-wrap gap-1.5">
          {(
            [
              ["all", `All (${rows.length})`],
              ["alerts_on", `Alerts On (${rows.filter((r) => r.alerts_enabled).length})`],
              ["alerts_off", `Alerts Off (${rows.filter((r) => !r.alerts_enabled).length})`],
            ] as [Tab, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setTab(value)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                tab === value
                  ? "bg-brand text-brown-dark shadow-soft"
                  : "bg-secondary text-muted-foreground hover:bg-brand-soft"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading your saved searches…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            title="No saved searches yet"
            description="Set your filters on the map and tap “Save Search” to store them here."
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {visible.map((s) => (
              <li
                key={s.id}
                className="flex flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-soft"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-brown-dark">{s.name}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Saved {new Date(s.created_at).toLocaleDateString()}
                      {s.match_count > 0 &&
                        ` · ${s.match_count} match${s.match_count === 1 ? "" : "es"}`}
                      {s.last_alert_at &&
                        ` · last alert ${new Date(s.last_alert_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${
                      s.alerts_enabled
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {s.alerts_enabled ? "Alerts On" : "Alerts Off"}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {describeSavedSearch(s).map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-brown-dark"
                    >
                      {chip}
                    </span>
                  ))}
                </div>

                <div className="mt-auto flex flex-wrap gap-1.5">
                  <button
                    onClick={() => alertM.mutate({ id: s.id, enabled: !s.alerts_enabled })}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
                  >
                    {s.alerts_enabled ? (
                      <>
                        <BellOff className="h-3.5 w-3.5" /> Disable Alert
                      </>
                    ) : (
                      <>
                        <Bell className="h-3.5 w-3.5" /> Enable Alert
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setRenaming(s);
                      setRenameValue(s.name);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Rename
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete “${s.name}”?`)) deleteM.mutate(s.id);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold text-destructive transition hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {renaming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-4 shadow-float">
            <h2 className="text-sm font-bold text-brown-dark">Rename saved search</h2>
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setRenaming(null)}
                className="rounded-xl border border-border px-3 py-2 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                disabled={renameM.isPending}
                onClick={() => renameM.mutate({ id: renaming.id, name: renameValue })}
                className="rounded-xl bg-brand px-3 py-2 text-sm font-semibold text-brown-dark shadow-soft transition hover:brightness-105 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
